import { c as _c } from "react/compiler-runtime";
import { DropdownMenu } from '@common/components';
import { assert, cn } from '@core/utils';
import type { GiftOrderProductReviewDto } from '@generated/unauth';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { UnauthApi, UnauthInfiniteQueryOptions, UnauthQueryKey } from '@/api';
import { InfiniteListWithMoreButton } from '@/app/donation/_components/InfiniteListWithMoreButton';
import { PhotoReviewSwiper } from '@/app/donation/_components/PhotoReviewSwiper';
import { ReviewCard } from '@/app/donation/_components/ReviewCard';
import { ReviewDetailSessionPortal } from '@/app/donation/_components/ReviewDetailSessionPortal';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';
import { DEFAULT_GIFT_REVIEW_SORT, HOMETOWN_SECTION_PAGE_SIZE } from '../../constants';
import { InfiniteListMoreButton } from '../InfiniteListMoreButton';
interface ReviewsContentProps {
  institutionId: number;
  initialData?: GiftOrderProductReviewDto[];
}
type SortValue = 'LATEST' | 'RATING_DESC';
const SORT_OPTIONS: {
  value: SortValue;
  label: string;
}[] = [{
  value: 'LATEST',
  label: '최신등록순'
}, {
  value: 'RATING_DESC',
  label: '평점 높은순'
}];

/** 포토후기만 좁게 서스펜드해 후기 섹션 전체가 스피너로 비는 것을 막는다 */
const PhotoReviews = t0 => {
  const $ = _c(11);
  const {
    institutionId
  } = t0;
  let t1;
  if ($[0] !== institutionId) {
    t1 = filledInfiniteQueryOptions(UnauthInfiniteQueryOptions["get:/wello/v2/allows/home/gift-review/photo"]({
      query: {
        institutionId,
        page: 1,
        size: 10
      }
    }));
    $[0] = institutionId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const photoInfiniteQueryOptions = t1;
  const {
    queryFn
  } = photoInfiniteQueryOptions;
  assert(typeof queryFn === "function");
  let t2;
  if ($[2] !== photoInfiniteQueryOptions || $[3] !== queryFn) {
    t2 = {
      ...photoInfiniteQueryOptions,
      queryFn
    };
    $[2] = photoInfiniteQueryOptions;
    $[3] = queryFn;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data
  } = useSuspenseInfiniteQuery(t2);
  const firstPage = data.pages[0]?.context;
  let t3;
  if ($[5] !== firstPage?.contents) {
    t3 = firstPage?.contents ?? [];
    $[5] = firstPage?.contents;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = firstPage?.totalCount ?? 0;
  let t5;
  if ($[7] !== institutionId || $[8] !== t3 || $[9] !== t4) {
    t5 = <PhotoReviewSwiper photoReviewList={t3} photoReviewCount={t4} institutionId={institutionId} />;
    $[7] = institutionId;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
export const ReviewsContent = t0 => {
  const $ = _c(24);
  const {
    institutionId,
    initialData
  } = t0;
  const [selectedSort, setSelectedSort] = useState(DEFAULT_GIFT_REVIEW_SORT);
  const [detailReviewId, setDetailReviewId] = useState();
  let t1;
  if ($[0] !== institutionId) {
    t1 = <Suspense fallback={null}><PhotoReviews institutionId={institutionId} /></Suspense>;
    $[0] = institutionId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("my-12 flex items-center justify-end px-20", "mobile-sm:hidden");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== selectedSort) {
    t3 = <div className={t2}><DropdownMenu type="text" value={selectedSort} options={SORT_OPTIONS} onChange={setSelectedSort} align="end" contentClassName="!min-w-160" /></div>;
    $[3] = selectedSort;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <InfiniteListMoreButton className={cn("mt-40", "mobile-tablet:mx-20 mobile-tablet:mt-12")} />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  if ($[6] !== institutionId || $[7] !== selectedSort) {
    t5 = [...UnauthQueryKey["get:/wello/v2/allows/home/gift-review"](), selectedSort, institutionId, HOMETOWN_SECTION_PAGE_SIZE];
    t6 = t7 => {
      const {
        pageParam
      } = t7;
      return UnauthApi["get:/wello/v2/allows/home/gift-review"]({
        query: {
          institutionId,
          sortType: selectedSort,
          page: pageParam,
          size: HOMETOWN_SECTION_PAGE_SIZE
        }
      }).then(_temp);
    };
    $[6] = institutionId;
    $[7] = selectedSort;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = item => <ReviewCard rating={item.rating} createdAt={item.createdAt} productName={item.productName} contents={item.contents} reviewer={item.nickname} imageUrlList={item.fileUrlList} answer={item.answer} answerAt={item.answerAt} onSelect={() => setDetailReviewId(item.reviewId)} />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== initialData || $[12] !== t5 || $[13] !== t6) {
    t8 = <div className="flex flex-col"><InfiniteListWithMoreButton initialData={initialData} moreButtonComponent={t4} moreButtonClassName="px-20" queryKey={t5} queryFn={t6} pageSize={HOMETOWN_SECTION_PAGE_SIZE} renderItem={t7} getItemKey={_temp2} /></div>;
    $[11] = initialData;
    $[12] = t5;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = !!detailReviewId;
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setDetailReviewId(undefined);
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== detailReviewId || $[17] !== t9) {
    t11 = <ReviewDetailSessionPortal open={t9} onClose={t10} reviewId={detailReviewId} />;
    $[16] = detailReviewId;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t1 || $[20] !== t11 || $[21] !== t3 || $[22] !== t8) {
    t12 = <div>{t1}{t3}{t8}{t11}</div>;
    $[19] = t1;
    $[20] = t11;
    $[21] = t3;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
function _temp(data) {
  return data.data?.context?.contents ?? [];
}
function _temp2(item_0) {
  return item_0.reviewId;
}